import React, { useEffect, useState } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Box,
    Typography,
    TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppConfirm } from '../contexts/AppConfirmContext';

const NAME_REGEX = /^[a-zA-Z]+$/;

function validateJson(str) {
    return true;
    // try {
    //     JSON.parse(str);
    //     return true;
    // } catch {
    //     return false;
    // }
}

export default function SourcesList({ ruleSetId, refreshSources, setRefreshSources, dialogOpen, setDialogOpen, dialogSignal }) {
    const [sources, setSources] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0); // TablePagination is zero-based
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const { confirm } = useAppConfirm();

    // Form dialog state
    const [form, setForm] = useState({
        id: null,
        name: '',
        fields: '',
        keyReference: ''
    });
    const [jsonError, setJsonError] = useState('');

    // Fetch sources
    useEffect(() => {
        setLoading(true);
        fetch(`/api/ruleSet/${ruleSetId}/sources?page=${page + 1}&pageSize=${pageSize}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(res => res.json())
            .then(data => {
                setSources(data.items);
                setTotal(data.total);
                setLoading(false);
            });
    }, [page, pageSize, ruleSetId, refreshSources]);

    useEffect(() => { if (dialogSignal > 0) handleOpenDialog(null) }, [dialogSignal]);

    // Open dialog for new or edit
    const handleOpenDialog = (source = null) => {
        if (source) {
            setForm({
                id: source.id,
                name: source.name,
                fields: source.fields,
                keyReference: source.keyReference
            });
        } else {
            setForm({
                name: '',
                fields: '',
                keyReference: ''
            });
        }
        setJsonError('');
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Save (create or update)
    const handleSave = async () => {
        if (!validateJson(form.fields)) {
            setJsonError('Invalid JSON');
            return;
        }
        const payload = {
            name: form.name,
            fields: form.fields,
            keyReference: form.keyReference
        };
        let url = `/api/ruleSet/${ruleSetId}/sources`;
        let method = 'POST';
        if (form.id) {
            url += `/${form.id}`;
            method = 'PUT';
        }
        await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
        });
        setDialogOpen(false);
        setRefreshSources(prev => prev + 1);

    };

    // Delete
    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete this source?',
            content: 'Are you sure you want to delete source? This process will be permanent',
            confirmText: 'Accept',
            cancelText: 'Cancel'
        });
        if (confirmed) {
            let resp = await fetch(`/api/ruleSet/${ruleSetId}/sources/${id}`, { method: 'DELETE', credentials: 'include' });
            if (resp.status === 204) {
                setRefreshSources(prev => prev + 1);
            }
        }
    };


    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <List>
                {sources.length === 0 && !loading && (
                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                        No sources found.
                    </Typography>
                )}
                {sources.map((source) => (
                    <ListItem
                        key={source.id}
                        sx={{
                            borderRadius: 2,
                            mb: 1,
                            boxShadow: 1,
                            bgcolor: 'background.paper',
                            '&:hover': { boxShadow: 3, bgcolor: 'grey.100' }
                        }}
                        secondaryAction={
                            <Box>
                                <IconButton edge="end" aria-label="edit" onClick={() => handleOpenDialog(source)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton edge="end" aria-label="delete" color="error" onClick={() => handleDelete(source.id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        }
                    >
                        <ListItemText
                            primary={source.name}
                            secondary={source.type}
                        />
                    </ListItem>
                ))}
            </List>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20, 50]}
            />
            {/* Form Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                <DialogTitle>{form.id ? 'Edit Source' : 'Add Source'}</DialogTitle>
                <DialogContent>
                    <Alert severity="info">
                        Only letters are allowed for Source Name (no spaces, numbers or special characters).<br />
                        This is to ensure compatibility with variable replacements in workflow steps.
                    </Alert>
                    <TextField
                        label="Source Name"
                        value={form.name}
                        onChange={e => {
                            const val = e.target.value;
                            if (val === '' || NAME_REGEX.test(val)) {
                                setForm({ ...form, name: val });
                            }
                        }}
                        fullWidth
                        required
                        margin="normal"
                    />
                    <Alert severity="warning">
                        Source Step Name is the workflow step name to indicate where the data is extracted in case data is coming from an output of an action.<br />
                        If you ommit this field or leave it with a name step that is not present in the workflow, variable replacements for <b>${"{variable.path}"}</b> will not work
                    </Alert>
                    <TextField
                        label="Source Step Name"
                        value={form.keyReference}
                        onChange={e => setForm({ ...form, keyReference: e.target.value })}
                        fullWidth
                        margin="normal"
                    />
                    <Alert severity="info">
                        Payload Data is the data sent to execution of the workflow and is the general Input Data.<br />
                        Workflow Step Data is the data that is generated as an output of workflow step.<br />
                        To include <b>Payload</b> variables use the format: <b>@{"{variable.path}"}</b> <br />
                        To include <b>Workflow Step</b> variables use the format: <b>${"{variable.path}"}</b>
                    </Alert>
                    <Alert severity="info">
                        Example object for input:<br />
                        {}
                    </Alert>
                    <TextField
                        label="Fields (JSON)"
                        value={form.fields}
                        onChange={e => {
                            setForm({ ...form, fields: e.target.value });
                            setJsonError(validateJson(e.target.value) ? '' : 'Invalid JSON');
                        }}
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={4}
                        error={!!jsonError}
                        helperText={jsonError || 'Enter valid JSON'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} disabled={(jsonError !== '' || !form.name)} variant="contained" color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
